import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "../lib/gsap";

/**
 * Vanta.js HALO background — Light Cosmic Luxury: alabaster base (#FDFCF9)
 * with soft Royal Blue (#1E3A8A) + Muted Gold (#D4AF37) celestial accents.
 *
 * INTEGRATION:
 *  - three.js + vanta are dynamically imported (not top-level static
 *    imports) so their ~680KB (three.js alone) never enters the app's main
 *    JS bundle that every visitor downloads before first paint — this is
 *    purely decorative WebGL behind Hero's own CSS gradient background
 *    (see Hero.tsx's radial-gradient layers, which render immediately and
 *    already make the section visually complete without WebGL), so it's a
 *    safe deferred/optional enhancement rather than critical content. See
 *    docs/seo-trust-audit.md's Phase 1 Web Vitals item — this was the
 *    single largest contributor to the pre-existing ~540KB main bundle
 *    (three.module.js alone was ~660KB of mapped source per
 *    source-map-explorer).
 *  - THREE injected explicitly into HALO (no double-bundle of three.js).
 *  - destroy() on unmount to release the WebGL context.
 *  - prefers-reduced-motion: skip WebGL entirely (including the dynamic
 *    import — no point fetching three.js/vanta at all), leave the static
 *    CSS deep-space base.
 */
export function useVantaHalo() {
  const ref = useRef<HTMLDivElement>(null);
  const effect = useRef<{ destroy: () => void } | null>(null);

  useEffect(() => {
    if (prefersReducedMotion() || !ref.current) return;
    let cancelled = false;

    Promise.all([import("three"), import("vanta/dist/vanta.halo.min")]).then(([THREE, { default: HALO }]) => {
      if (cancelled || !ref.current) return;
      effect.current = HALO({
        el: ref.current,
        THREE,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        baseColor: 0xd4af37, // warm muted-gold swirl for the beige aura
        backgroundColor: 0xfdfcf9, // light alabaster / beige
        amplitudeFactor: 0.9,
        size: 1.3,
        xOffset: 0.1,
        yOffset: 0.0,
      });
    });

    return () => {
      cancelled = true;
      effect.current?.destroy();
      effect.current = null;
    };
  }, []);

  return ref;
}
